import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    Award,
    Users,
    Star,
    BookOpen,
    ArrowLeft,
    ArrowRight,
    Quote,
    ShieldCheck,
} from "lucide-react";
import Navbar from "../../../../components/navbar/Navbar";
import Footer from "../../../../components/footer/Footer";
import CallToActionBanner from "../../home/call-to-action-banner/CallToActionBanner";
import api from "../../../../utils/api";
import { testimonialApi } from "../../../../api/api";

const studentsData = [
    {
        id: 1,
        name: "Divyanshu Mishra",
        rank: "5",
        exam: "EPFO EO/AO 2023",
        gender: "male",
        photo: "/assets/male-placeholder.png",
    },
    {
        id: 2,
        name: "Piyush Kumar",
        rank: "6",
        exam: "EPFO EO/AO 2023",
        gender: "male",
        photo: "/assets/male-placeholder.png",
    },
    {
        id: 3,
        name: "Alok Gupta",
        rank: "28",
        exam: "EPFO EO/AO 2023",
        gender: "male",
        photo: "/assets/male-placeholder.png",
    },
    {
        id: 4,
        name: "Anoop Kumar Jena",
        rank: "42",
        exam: "EPFO EO/AO 2023",
        gender: "male",
        photo: "/assets/male-placeholder.png",
    },
    {
        id: 5,
        name: "Meera Beladiya",
        rank: "51",
        exam: "EPFO EO/AO 2023",
        gender: "female",
        photo: "/assets/female-placeholder.png",
    },
    {
        id: 6,
        name: "Mahanti Sandhyana",
        rank: "85",
        exam: "EPFO EO/AO 2023",
        gender: "female",
        photo: "/assets/female-placeholder.png",
    },
    {
        id: 7,
        name: "Vishal Gaurav",
        rank: "102",
        exam: "EPFO EO/AO 2023",
        gender: "male",
        photo: "/assets/male-placeholder.png",
    },
    {
        id: 8,
        name: "Sachin",
        rank: "277",
        exam: "EPFO EO/AO 2023",
        gender: "male",
        photo: "/assets/male-placeholder.png",
    },
    {
        id: 9,
        name: "Himanshu Chandra Goshi",
        rank: "284",
        exam: "EPFO EO/AO 2023",
        gender: "male",
        photo: "/assets/male-placeholder.png",
    },
    {
        id: 10,
        name: "Mohd Sameer",
        rank: "325",
        exam: "EPFO EO/AO 2023",
        gender: "male",
        photo: "/assets/male-placeholder.png",
    },
    {
        id: 11,
        name: "Bankar Akshay Baliram",
        rank: "360",
        exam: "EPFO EO/AO 2023",
        gender: "male",
        photo: "/assets/male-placeholder.png",
    },
    {
        id: 12,
        name: "Kshitij Nayar",
        rank: "366",
        exam: "EPFO EO/AO 2023",
        gender: "male",
        photo: "/assets/male-placeholder.png",
    },
    {
        id: 13,
        name: "Akarsh Khare",
        rank: "406",
        exam: "EPFO EO/AO 2023",
        gender: "male",
        photo: "/assets/male-placeholder.png",
    },
    {
        id: 14,
        name: "Srishti Gupta",
        rank: "262",
        exam: "EPFO EO/AO 2023",
        gender: "female",
        photo: "/assets/female-placeholder.png",
    },
];

const Selections = () => {
    const [testimonials, setTestimonials] = useState([]);
    const [testIndex, setTestIndex] = useState(0);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        api.get(`${testimonialApi}`)
            .then((res) => setTestimonials(res?.data?.data || []))
            .catch((err) => console.error("Testimonial API Error:", err))
            .finally(() => setLoading(false));
    }, []);

    return (
        <div className="bg-[#fcfcfc] min-h-screen">
            

            {/* --- Authoritative Hero --- */}
            <section className="pt-24 pb-16 px-4 bg-neutral-900 relative overflow-hidden">
                <div className="absolute top-0 left-0 w-full h-full opacity-10 pointer-events-none bg-[radial-gradient(#e11d48_1px,transparent_1px)] [background-size:32px_32px]"></div>

                <div className="max-w-7xl mx-auto text-center relative z-10">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.6 }}
                    >
                        <h1 className="text-4xl md:text-7xl font-black text-white leading-tight mb-4">
                            Faces of{" "}
                            <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-yellow-600">
                                Dominance.
                            </span>
                        </h1>
                        <p className="text-gray-400 text-lg md:text-xl max-w-2xl mx-auto font-medium">
                            We don't just teach; we produce officers. Witness
                            the legacy of PYQ Pedia.
                        </p>
                    </motion.div>
                </div>
            </section>

            {/* --- Hall of Fame Portrait Grid --- */}
            <section className="py-20 px-4 max-w-7xl mx-auto">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-y-16 gap-x-8">
                    {studentsData.map((student, idx) => (
                        <motion.div
                            key={student.id}
                            initial={{ opacity: 0, y: 30 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{
                                delay: idx * 0.1,
                                type: "spring",
                                stiffness: 100,
                            }}
                            className="relative group"
                        >
                            {/* Massive AIR Rank Highlight */}
                            <div className="absolute -top-8 -left-2 z-20 transition-transform duration-500 group-hover:-translate-y-2">
                                <div className="bg-neutral-900 text-white border-2 border-yellow-500 px-4 py-2 flex flex-col items-center shadow-2xl">
                                    <span className="text-[10px] font-bold text-yellow-500 uppercase tracking-widest leading-none mb-1">
                                        RANK
                                    </span>
                                    <span className="text-3xl font-black leading-none">
                                        {student.rank}
                                    </span>
                                </div>
                            </div>

                            {/* Achiever Portrait */}
                            <div className="relative aspect-[4/5] bg-gray-200 overflow-hidden border-[6px] border-white shadow-xl mb-6">
                                {/* Visual effect layer */}
                                <div className="absolute inset-0 bg-red-900/10 mix-blend-overlay group-hover:bg-transparent transition-all duration-500"></div>
                                <img
                                    src={student.photo}
                                    alt={student.name}
                                    className="w-full h-full object-cover transition-all duration-700 group-hover:scale-110"
                                />
                                {/* Verified Overlay */}
                                <div className="absolute bottom-4 right-4 text-white opacity-40 group-hover:opacity-100 transition-opacity">
                                    <ShieldCheck size={24} />
                                </div>
                            </div>

                            {/* Student Metadata */}
                            <div className="flex flex-col text-left border-l-4 border-red-700 pl-4">
                                <h3 className="text-xl font-black text-neutral-900 leading-none mb-2 group-hover:text-red-700 transition-colors">
                                    {student.name}
                                </h3>
                                <p className="text-[11px] font-bold text-gray-500 uppercase tracking-wider mb-1">
                                    {student.exam}
                                </p>
                                <div className="w-12 h-1 bg-yellow-500 rounded-full mt-2"></div>
                            </div>
                        </motion.div>
                    ))}
                </div>
            </section>

            {/* --- Performance Summary Section --- */}
            <section className="py-24 bg-slate-50">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
                        {[
                            {
                                label: "Selected Officers",
                                val: "1000+",
                                icon: <Users className="text-red-600" />,
                            },
                            {
                                label: "Top AIR Results",
                                val: "100+",
                                icon: <Award className="text-red-600" />,
                            },
                            {
                                label: "Selection Rate",
                                val: "95%",
                                icon: <Star className="text-red-600" />,
                            },
                            {
                                label: "Academic Trust",
                                val: "10+ Yrs",
                                icon: <BookOpen className="text-red-600" />,
                            },
                        ].map((stat, i) => (
                            <div
                                key={i}
                                className="flex flex-col items-center text-center p-8 bg-white rounded-3xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
                            >
                                <div className="mb-4">{stat.icon}</div>
                                <h4 className="text-4xl font-sans font-bold text-neutral-900  mb-1">
                                    {stat.val}
                                </h4>
                                <p className="text-xs font-bold text-gray-400 uppercase tracking-[0.2em]">
                                    {stat.label}
                                </p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* --- Achievers Testimonials (Premium Stack) --- */}
            {testimonials.length > 0 && (
                <section className="py-24 px-4 bg-white overflow-hidden">
                    <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
                        <div className="text-left">
                            <div className="flex items-center gap-3 text-red-700 font-black text-xs uppercase tracking-widest mb-6">
                                <span className="w-12 h-1 bg-red-700"></span>
                                Voice of Success
                            </div>
                            <h2 className="text-4xl md:text-5xl font-black text-neutral-900 mb-8 leading-tight">
                                Insights from the{" "}
                                <span className="text-red-700">Champions.</span>
                            </h2>
                            <div className="flex gap-4">
                                <button
                                    onClick={() =>
                                        setTestIndex((prev) =>
                                            prev === 0
                                                ? testimonials.length - 1
                                                : prev - 1,
                                        )
                                    }
                                    className="w-14 h-14 border-2 border-neutral-900 flex items-center justify-center hover:bg-neutral-900 hover:text-white transition-all group"
                                >
                                    <ArrowLeft size={24} />
                                </button>
                                <button
                                    onClick={() =>
                                        setTestIndex(
                                            (prev) =>
                                                (prev + 1) %
                                                testimonials.length,
                                        )
                                    }
                                    className="w-14 h-14 border-2 border-neutral-900 flex items-center justify-center hover:bg-neutral-900 hover:text-white transition-all group"
                                >
                                    <ArrowRight size={24} />
                                </button>
                            </div>
                        </div>

                        <div className="relative">
                            <AnimatePresence mode="wait">
                                <motion.div
                                    key={testIndex}
                                    initial={{ opacity: 0, x: 50 }}
                                    animate={{ opacity: 1, x: 0 }}
                                    exit={{ opacity: 0, x: -50 }}
                                    transition={{
                                        type: "spring",
                                        stiffness: 100,
                                        damping: 20,
                                    }}
                                    className="bg-neutral-900 p-10 md:p-14 rounded-[3rem] relative z-10 shadow-2xl"
                                >
                                    <Quote
                                        className="text-yellow-500/20 absolute top-10 left-10"
                                        size={80}
                                    />
                                    <div
                                        className="text-white text-lg md:text-xl font-medium leading-relaxed italic mb-10 relative z-10"
                                        dangerouslySetInnerHTML={{
                                            __html: testimonials[testIndex]
                                                .message,
                                        }}
                                    />
                                    <div className="flex items-center gap-5">
                                        <img
                                            src={testimonials[testIndex].image}
                                            className="w-16 h-16 rounded-full border-4 border-neutral-800 object-cover"
                                            alt={testimonials[testIndex].name}
                                        />
                                        <div>
                                            <h4 className="font-bold text-white text-lg leading-none">
                                                {testimonials[testIndex].name}
                                            </h4>
                                            <p className="text-red-500 font-bold text-xs uppercase tracking-widest mt-2">
                                                {
                                                    testimonials[testIndex]
                                                        .designation
                                                }
                                            </p>
                                        </div>
                                    </div>
                                </motion.div>
                            </AnimatePresence>
                            {/* Success Backdrop Accents */}
                            <div className="absolute top-6 left-6 w-full h-full bg-red-600 rounded-[3rem] -z-10 opacity-20 transform translate-x-4 translate-y-4"></div>
                        </div>
                    </div>
                </section>
            )}

            <CallToActionBanner />
            
        </div>
    );
};

export default Selections;
